import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import useInterveneSx from '../../hooks/useInterveneSx';
import useLeadDetailsList from '../../hooks/useLeadDetailsList';

const leadDateFormatter = new Intl.DateTimeFormat('en-IN', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
});

function getLeadKey(lead) {
  return lead?.id || lead?.lead_id || '';
}

function getLeadLabel(lead) {
  return lead?.lead_id || lead?.id || 'Untitled lead';
}

function getLeadFieldValue(lead, keyName) {
  return lead?.data?.find((field) => field?.key_name === keyName)?.value || '';
}

function formatLeadDate(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return leadDateFormatter.format(date);
}

function getLeadMeta(lead) {
  return [
    getLeadFieldValue(lead, 'property_type'),
    lead?.last_status?.status_name,
    formatLeadDate(lead?.created_at),
  ].filter(Boolean).join(' • ');
}

export default function LeadDetailsLeadList({
  phone,
  campaignId,
  leadFormId,
  enabled,
  selectedLeadId,
  refreshToken,
  canCreateLead = false,
  onLeadSelect,
  onCreateLead,
}) {
  const styles = useInterveneSx().contextDrawer;
  const {
    leads,
    isLoading,
    isLoadingMore,
    hasMore,
    notFound,
    handleScroll,
  } = useLeadDetailsList({
    phone,
    campaignId,
    leadFormId,
    enabled,
    refreshToken,
  });

  if (isLoading && leads.length === 0) {
    return (
      <Box sx={styles.leadListSection}>
        <Box sx={styles.leadListLoading}>
          <CircularProgress size={24} />
        </Box>
      </Box>
    );
  }

  if (!isLoading && (notFound || leads.length === 0)) {
    return (
      <Box sx={styles.leadListSection}>
        <Box sx={styles.leadListEmpty}>
          <Typography variant="subtitle2">No leads yet</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1.5 }}>
            No leads found for this customer on the selected form.
          </Typography>
          {canCreateLead && (
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={onCreateLead}
            >
              Create first lead
            </Button>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={styles.leadListSection}>
      <Box sx={styles.leadListSectionHeader}>
        <Box>
          <Typography sx={styles.leadListSectionTitle}>Leads</Typography>
          <Typography sx={styles.leadListSectionDescription}>
            Select a lead to review or update its details.
          </Typography>
        </Box>
        {canCreateLead && (
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={onCreateLead}
            sx={styles.leadListNewButton}
          >
            New Lead
          </Button>
        )}
      </Box>

      <Box sx={styles.leadListScrollCompact} onScroll={handleScroll}>
        <List disablePadding sx={styles.leadList}>
          {leads.map((lead) => {
            const leadKey = getLeadKey(lead);
            const isSelected = selectedLeadId && `${selectedLeadId}` === `${leadKey}`;

            return (
              <ListItem key={leadKey} disablePadding>
                <ListItemButton
                  selected={Boolean(isSelected)}
                  onClick={() => onLeadSelect?.(lead)}
                  sx={styles.leadListItem}
                >
                  <ListItemText
                    primary={getLeadLabel(lead)}
                    secondary={getLeadMeta(lead) || 'No additional details'}
                    primaryTypographyProps={{ noWrap: true, fontSize: '13px', fontWeight: 600 }}
                    secondaryTypographyProps={{ noWrap: true, fontSize: '11px' }}
                  />
                  <ChevronRightIcon sx={styles.leadListChevron} />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>

        {(isLoadingMore || hasMore) && (
          <Box sx={styles.leadListFooter}>
            {isLoadingMore ? (
              <CircularProgress size={20} />
            ) : (
              <Typography variant="caption" color="text.secondary">
                Scroll for more
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}
